import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import TrustBar from "@/sections/TrustBar";
import AtlasShowcase from "@/sections/lexos/AtlasShowcase";
import AuditorShowcase from "@/sections/lexos/AuditorShowcase";
import AnalyticsShowcase from "@/sections/lexos/AnalyticsShowcase";
import FirmEvolution from "@/sections/about/FirmEvolution";
import { LEXOS_PILLARS, AGENT_FIRST, HUMAN_FIRST, STATUS_META } from "@/sections/nav/navData";

/* LexOS — operating-system marketing page. Scaffold with hero + pillars + agent / human columns.
   Detailed copy & visuals coming later. */

const LexOSPage = () => {
    useEffect(() => { document.title = "LexOPS — Lexafide"; }, []);

    return (
        <main data-testid="lexos-page" className="relative">
            {/* Hero */}
            <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-[var(--cream)]">
                <div className="max-w-[1080px] mx-auto px-6 md:px-10 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                        className="eyebrow-pill inline-flex items-center gap-2"
                    >
                        <i className="fa-solid fa-microchip text-[10px] accent-blue" />
                        Introducing LexOPS
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
                        className="font-display text-[clamp(2.4rem,6vw,4.6rem)] leading-[1.02] tracking-tight font-semibold text-[var(--ink)] mt-6"
                    >
                        The operating system for the{" "}
                        <span className="accent-blue">AI-native legal ops team.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
                        className="mt-7 text-lg md:text-xl text-[var(--ink-soft)] leading-relaxed max-w-2xl mx-auto"
                    >
                        Lexafide runs the operational work — intake, follow-ups, voicemails, status updates — and hands your team the cases that need a human. Same staff. 3× the throughput.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
                        className="mt-9 flex flex-col sm:flex-row gap-3 justify-center"
                    >
                        <a
                            href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                            target="_blank" rel="noreferrer"
                            data-testid="lexos-cta-demo"
                            className="btn-primary inline-flex items-center justify-center gap-2 text-sm"
                        >
                            Book a 30-min demo
                            <i className="fa-solid fa-arrow-right text-[10px]" />
                        </a>
                        <Link
                            to="/#problem"
                            data-testid="lexos-cta-see-it"
                            className="btn-ghost inline-flex items-center justify-center gap-2 text-sm"
                        >
                            See it in action
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Trust marquee */}
            <TrustBar />

            {/* Lexafide Operations Maturity framework + diagnosis CTA */}
            <FirmEvolution />

            {/* Pillars — LexOPS core */}
            <Section label="The platform" headline="LexOPS" highlight="four pillars." description="The foundation that turns every call, document, and case into structured, current, actionable intelligence.">
                <Grid items={LEXOS_PILLARS} accent />
            </Section>

            {/* LexGraph — deep dive */}
            <AtlasShowcase />

            {/* Auditor — deep dive */}
            <AuditorShowcase />

            {/* Analytics — deep dive */}
            <AnalyticsShowcase />

            {/* Agent-first agents */}
            <Section
                label="Agent-first"
                headline="Autonomous workers that"
                highlight="never need a coffee break."
                description="Hand off the high-volume, low-judgment work to agents that operate 24/7 across calls, follow-ups, and outbound communications. Every action logged, every handoff clean."
                tone="blue"
            >
                <Grid items={AGENT_FIRST} />
            </Section>

            {/* Human-first tools */}
            <Section
                label="Human-first"
                headline="Co-pilots that"
                highlight="amplify the judgment work."
                description="When a human has to be in the loop, Lexafide compresses the prep time. Paralegals, case managers, and attorneys keep the decisions; we keep the prep ready."
                tone="olive"
            >
                <Grid items={HUMAN_FIRST} />
            </Section>

            {/* CTA */}
            <section className="py-24 md:py-32 bg-[var(--cream-2)]">
                <div className="max-w-[920px] mx-auto px-6 md:px-10 text-center">
                    <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)]">
                        Ready to go <span className="accent-blue">AI-native</span> in 90 days?
                    </h2>
                    <p className="mt-5 text-lg text-[var(--ink-soft)]">
                        We&apos;ll map your firm&apos;s workflows, quantify the hours Lexafide reclaims, and ship a low-cost pilot — usually in under two weeks.
                    </p>
                    <a
                        href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                        target="_blank" rel="noreferrer"
                        data-testid="lexos-final-cta"
                        className="btn-primary inline-flex items-center gap-2 text-sm mt-8"
                    >
                        Schedule your discovery call
                        <i className="fa-solid fa-arrow-right text-[10px]" />
                    </a>
                </div>
            </section>
        </main>
    );
};

/* ---------- Section wrapper ---------- */
const Section = ({ label, headline, highlight, description, children, tone }) => {
    const bg = tone === "blue" ? "bg-[var(--cream)]" : tone === "olive" ? "bg-[var(--cream-2)]" : "bg-[var(--cream)]";
    return (
        <section className={`py-20 md:py-28 ${bg}`}>
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.65 }}
                    className="mb-12 md:mb-14 max-w-2xl"
                >
                    <span className="eyebrow-pill">{label}</span>
                    <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5">
                        {headline}{" "}
                        <span className="accent-blue">{highlight}</span>
                    </h2>
                    {description && (
                        <p className="mt-5 text-base md:text-lg text-[var(--ink-soft)] leading-relaxed">
                            {description}
                        </p>
                    )}
                </motion.div>
                {children}
            </div>
        </section>
    );
};

/* ---------- Grid of cards ---------- */
const Grid = ({ items, accent }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {items.map((it, i) => {
            const status = STATUS_META[it.status] || STATUS_META.soon;
            const isAccent = accent && it.accent;
            return (
                <motion.div
                    key={it.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.55, delay: 0.04 * i }}
                    className={`group relative rounded-2xl border p-6 transition-all flex flex-col ${
                        isAccent
                            ? "border-[var(--blue)]/40 bg-[rgba(30,91,255,0.04)]"
                            : "border-[var(--border-strong)] bg-[var(--paper)] hover:border-[var(--blue)]/30 hover:shadow-[0_18px_40px_-22px_rgba(35,31,35,0.25)]"
                    }`}
                >
                    {/* Status pill */}
                    <span
                        className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-[0.14em] font-semibold"
                        style={{ background: status.bg, color: status.color }}
                    >
                        <span
                            className={`inline-block w-1.5 h-1.5 rounded-full ${it.status === "live" ? "animate-pulse" : ""}`}
                            style={{ background: status.dot }}
                        />
                        {status.label}
                    </span>

                    <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                            isAccent
                                ? "bg-[var(--blue)] text-white"
                                : "bg-[var(--cream-2)] text-[var(--ink)] group-hover:bg-[var(--blue)] group-hover:text-white"
                        }`}
                    >
                        <i className={`fa-solid ${it.icon} text-[16px]`} />
                    </div>
                    <div className="font-display text-lg font-semibold text-[var(--ink)] leading-tight">
                        {it.name}
                    </div>
                    <p className="text-sm text-[var(--ink-soft)] mt-2 leading-snug">
                        {it.tagline}
                    </p>
                    {it.features?.length > 0 && (
                        <ul className="mt-4 pt-4 border-t border-[var(--border)] space-y-2 text-[12.5px] text-[var(--ink-soft)] leading-snug">
                            {it.features.map((f) => (
                                <li key={f} className="flex items-start gap-2">
                                    <i className="fa-solid fa-check text-[10px] accent-blue mt-1 flex-shrink-0" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Lexafide pillar mark — sits subtly in the bottom-right corner */}
                    <img
                        src="/brand/lexafide-icon.png"
                        alt=""
                        aria-hidden
                        className="absolute bottom-4 right-4 w-5 h-5 rounded-[4px] opacity-30 group-hover:opacity-70 transition-opacity duration-300 pointer-events-none"
                        loading="lazy"
                        draggable={false}
                    />
                </motion.div>
            );
        })}
    </div>
);

export default LexOSPage;
